import { useEffect, useRef, useState } from 'react';

import {
  EqualIcon,
  Modal,
  Pagination,
  RemoveIcon,
  Text,
  type TypeTextProps,
  toastService,
} from '@thefarmersfront/kitchen';

import { Table, TableTopField } from '~/components/common';

import { useAPI } from '~/hooks/common';
import { useLoadingStore } from '~/store';
import { formatDate, formatNumber, getSettlementStorageDailyFeeDetailColumnDefs } from '~/utils';

import { TOAST_MESSAGE } from '~/constants';

import type {
  TypeGetStorageFeeDailySkusParams,
  TypeGetStorageFeeDailySkusResponse,
  TypeGetStorageFeeDailyTotalParams,
  TypeGetStorageFeeDailyTotalResponse,
} from '~/types/settlement.types';

import styles from './StorageDailyFeeDetailModal.module.scss';

const PAGE_SIZE = 100;

type TypeStorageDailyFeeDetailModalProps = {
  date: Date;
  onClose: () => void;
};

const StorageDailyFeeDetailModal = ({ date, onClose }: TypeStorageDailyFeeDetailModalProps) => {
  const setIsLoading = useLoadingStore((state) => state.setIsLoading);

  const { gridOptions, columnDefs } = getSettlementStorageDailyFeeDetailColumnDefs();

  const isFirstFetchRef = useRef(true);

  const [localOpen, setLocalOpen] = useState(false);
  const [dailyStorageFeeSummary, setDailyStorageFeeSummary] = useState<
    TypeGetStorageFeeDailyTotalResponse['data'] | null
  >(null);
  const [dailyStorageFeeData, setDailyStorageFeeData] = useState<TypeGetStorageFeeDailySkusResponse['data'] | null>(
    null
  );
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalCount: 0,
    limit: PAGE_SIZE,
  });

  useEffect(() => {
    if (dailyStorageFeeSummary && dailyStorageFeeData) {
      setLocalOpen(true);
    }
  }, [dailyStorageFeeSummary, dailyStorageFeeData]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    const results = await Promise.allSettled([fetchStorageDailyFeeTotal(), fetchStorageDailyFeeSkus()]);

    const hasError = results.some((result) => result.status === 'rejected');
    if (hasError) {
      onClose();
      return;
    }

    isFirstFetchRef.current = false;
  };

  const getStorageFeeDailyTotal = useAPI<TypeGetStorageFeeDailyTotalParams, TypeGetStorageFeeDailyTotalResponse>({
    endpoint: '/api-inventory-agg/v1/storage-fee/daily/total',
    method: 'get',
  });

  const getStorageFeeDailySkus = useAPI<TypeGetStorageFeeDailySkusParams, TypeGetStorageFeeDailySkusResponse>({
    endpoint: '/api-inventory-agg/v1/storage-fee/daily/skus',
    method: 'get',
  });
  const fetchStorageDailyFeeTotal = async (feeDate: Date = date) => {
    try {
      const formattedDate = formatDate(feeDate, 'YYYYMMDD');

      const { data } = await getStorageFeeDailyTotal({ params: { feeDate: formattedDate ?? '' } });
      setDailyStorageFeeSummary(data);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: TOAST_MESSAGE.DEFAULT_ERROR,
      });

      if (isFirstFetchRef.current) {
        return Promise.reject(new Error());
      }
    }
  };

  const fetchStorageDailyFeeSkus = async (currentPage = 0) => {
    try {
      setIsLoading(true);

      const params = {
        feeDate: formatDate(date, 'YYYYMMDD') ?? '',
        cursor: currentPage,
        limit: PAGE_SIZE,
      };

      const { data, pagination } = await getStorageFeeDailySkus({ params });

      if (data.length === 0) {
        toastService.addToast({
          message: '조회 가능한 보관비가 없습니다.',
        });
        onClose();
        return;
      }

      setDailyStorageFeeData(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: pagination.cursor,
        totalCount: pagination.listTotal,
      }));
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: TOAST_MESSAGE.DEFAULT_ERROR,
      });

      if (isFirstFetchRef.current) {
        return Promise.reject(new Error());
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={`${formatDate(date, 'YYYY년 MM월 DD일')} 보관비 상세`}
      size="xlarge"
      open={localOpen}
      onConfirm={onClose}
    >
      <div className={styles['contents-container']}>
        {dailyStorageFeeSummary && (
          <div className={styles['summary-section']}>
            <SummaryItem label="보관비 합계" amount={{ value: dailyStorageFeeSummary.totalStorageFee }} />
            <RemoveIcon size={16} color="gray400" />
            <SummaryItem label="면제 금액" amount={{ value: dailyStorageFeeSummary.unchargedStorageFee }} />
            <EqualIcon size={16} color="gray400" />
            <SummaryItem
              label="실제 과금액"
              amount={{ value: dailyStorageFeeSummary.chargedStorageFee, color: 'error' }}
            />
          </div>
        )}
        <div className={styles['view-section']}>
          <TableTopField dataTotalCount={pagination.totalCount} />
          <Table
            className={styles['table']}
            data={dailyStorageFeeData}
            options={{
              columnDefs,
              ...gridOptions,
            }}
            rowStatusType="NO_DATA"
          />
        </div>
        {pagination.totalCount > 0 && (
          <div className={styles['view-actions-section']}>
            <Pagination
              currentPage={pagination.currentPage + 1}
              totalCount={pagination.totalCount ?? 0}
              pageSize={PAGE_SIZE}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                const updatedPage = e.currentTarget.value ? Number(e.currentTarget.value) - 1 : 0;
                fetchStorageDailyFeeSkus(updatedPage);
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default StorageDailyFeeDetailModal;

const SummaryItem = ({
  label,
  amount: { value, color = 'primary' },
}: {
  label: string;
  amount: { value: number; color?: TypeTextProps['color'] };
}) => {
  return (
    <div className={styles['summary-item']}>
      <Text className={styles['summary-item-label']} color="secondary">
        {label}
      </Text>
      <Text className={styles['summary-item-amount']} weight="semi-bold" color={color}>
        {formatNumber(value, '원')}
      </Text>
    </div>
  );
};
